import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid, GridColumn, GridRow, Segment } from 'semantic-ui-react';
import AppHelmet from 'app/components/AppHelmet';
import Hook from 'app/components/UIComponents/Hook';
import Gradient from 'app/components/UIComponents/Gradient';
import CardsList from 'app/components/UIComponents/CardsList';
import Card from 'app/components/UIComponents/CardsList/Card';
import Button from 'app/components/UIComponents/Button';
import { PRODUCT_PICOX_TR } from 'app/components/Form/SendRequestForm';
import ImgLogoPicoxTR from './images/logo-picoxia-tr.svg?url';
import timeIcon from './images/fast-colors.svg?url';
import radioIcon from './images/icon-radio.svg?url';
import vetIcon from './images/icon-vet.svg?url';
import zenIcon from './images/zen-colors.svg?url';
import ImgTeleradiologists from './images/teleradiologists.jpg';
import './styles.scss';

/**
 * Page For Picoxia Teleradiology
 */
function PicoxiaTeleradiologyPage() {
  const intl = useIntl();

  return (
    <div className="picoxia">
      <AppHelmet
        titleIntlID="picoxia-teleradiology.meta-title"
        descriptionIntlID="picoxia-teleradiology.meta-description"
      />

      <div className="picoxiaTeleradiology">
        <section id="introduction" className="container">
          <Grid stackable columns={2}>
            <GridColumn>
              <Grid stackable>
                <GridRow>
                  <GridColumn width={4}>
                    <img src={ImgLogoPicoxTR} alt="PicoxTR" />
                  </GridColumn>
                  <GridColumn width={12}>
                    <h1 className="title title--big">
                      <FormattedMessage id="picoxia-teleradiology.introduction.title" />
                    </h1>
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn>
                    <Hook>
                      <FormattedMessage id="picoxia-teleradiology.introduction.hook" />
                    </Hook>
                    <p>
                      <FormattedMessage id="picoxia-teleradiology.introduction.text" />
                    </p>
                  </GridColumn>
                </GridRow>
              </Grid>
            </GridColumn>
            <GridColumn verticalAlign="middle">
              <iframe
                src="https://www.youtube.com/embed/fjlY5S-wwkU?rel=0"
                title="PicoxTR"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              />
            </GridColumn>
          </Grid>
        </section>

        <section id="vedim-partnership" className="container">
          <Grid stackable columns={2}>
            <GridColumn>
              <img
                src={ImgTeleradiologists}
                alt={intl.formatMessage({
                  id: 'picoxia-teleradiology.vedim-partnership.img.teleradiologists.alt',
                })}
              />
            </GridColumn>
            <GridColumn verticalAlign="middle">
              <h2 className="title">
                <FormattedMessage id="picoxia-teleradiology.vedim-partnership.title" />
              </h2>
              <p>
                <FormattedMessage id="picoxia-teleradiology.vedim-partnership.text" />
              </p>
            </GridColumn>
          </Grid>
        </section>

        <Gradient />
        <section id="features" className="container">
          <CardsList>
            <Card>
              <Card.Icon icon={radioIcon} />
              <Card.Title>
                <FormattedMessage id="picoxia-teleradiology.features.full-integration.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxia-teleradiology.features.full-integration.text" />
              </p>
            </Card>
            <Card>
              <Card.Icon icon={vetIcon} />
              <Card.Title>
                <FormattedMessage id="picoxia-teleradiology.features.experienced-radiologists.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxia-teleradiology.features.experienced-radiologists.text" />
              </p>
            </Card>
            <Card>
              <Card.Icon icon={zenIcon} />
              <Card.Title>
                <FormattedMessage id="picoxia-teleradiology.features.security-privacy.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxia-teleradiology.features.security-privacy.text" />
              </p>
            </Card>
            <Card>
              <Card.Icon icon={timeIcon} />
              <Card.Title>
                <FormattedMessage id="picoxia-teleradiology.features.speed-efficiency.title" />
              </Card.Title>
              <p>
                <FormattedMessage id="picoxia-teleradiology.features.speed-efficiency.text" />
              </p>
            </Card>
          </CardsList>
        </section>

        <section id="how-it-works" className="container">
          <h2 className="title">Comment ça marche&nbsp;?</h2>
          <Grid stackable columns={3}>
            <GridRow>
              <GridColumn>
                <Segment>
                  <h3 className="title">1</h3>
                  <h4 className="title">
                    <FormattedMessage id="picoxia-teleradiology.how-it-works.1.title" />
                  </h4>
                  <FormattedMessage id="picoxia-teleradiology.how-it-works.1.text" />
                </Segment>
              </GridColumn>
              <GridColumn>
                <Segment>
                  <h3 className="title">2</h3>
                  <h4 className="title">
                    <FormattedMessage id="picoxia-teleradiology.how-it-works.2.title" />
                  </h4>
                  <FormattedMessage id="picoxia-teleradiology.how-it-works.2.text" />
                </Segment>
              </GridColumn>
              <GridColumn>
                <Segment>
                  <h3 className="title">3</h3>
                  <h4 className="title">
                    <FormattedMessage id="picoxia-teleradiology.how-it-works.3.title" />
                  </h4>
                  <FormattedMessage id="picoxia-teleradiology.how-it-works.3.text" />
                </Segment>
              </GridColumn>
            </GridRow>
          </Grid>
        </section>

        <section id="why-choose-us" className="container">
          <h2 className="title">
            <FormattedMessage id="picoxia-teleradiology.why-choose-us.title" />
          </h2>
          <ul>
            <li>
              <FormattedMessage id="picoxia-teleradiology.why-choose-us.simplicity.text" />
            </li>
            <li>
              <FormattedMessage id="picoxia-teleradiology.why-choose-us.precision.text" />
            </li>
            <li>
              <FormattedMessage id="picoxia-teleradiology.why-choose-us.rapidity.text" />
            </li>
            <li>
              <FormattedMessage id="picoxia-teleradiology.why-choose-us.security.text" />
            </li>
          </ul>
        </section>

        <section id="contact" className="container">
          <h2 className="title">
            <FormattedMessage id="picoxia-teleradiology.contact.title" />
          </h2>
          <p>
            <FormattedMessage id="picoxia-teleradiology.contact.text.1" />
          </p>
          <p>
            <FormattedMessage id="picoxia-teleradiology.contact.text.2" />
          </p>
          <div className="buttons">
            <Button url={`/request-demo?product=${PRODUCT_PICOX_TR}`}>
              <FormattedMessage id="picoxia-teleradiology.contact.title" />
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default PicoxiaTeleradiologyPage;
